import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CelebrationIcon from '@mui/icons-material/Celebration';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from '../../config/axiosInstance';
import { UserContext } from '../context/UserContext';
import useConfirm from '../calendar/dialogs/UseConfirm';


const MONTH_OPTIONS = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' },
];

const formatDayMonth = (month, day) =>
  `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}`;

const getDaysInMonth = (month, year) => new Date(year, month, 0).getDate();


export default function PublicHolidayConfigPage() {
  const { confirm, ConfirmDialog } = useConfirm();
  const { permissions } = useContext(UserContext);
  const canRead = permissions.includes('licenses.read');
  const canWrite = permissions.includes('licenses.write');

  const currentYear = new Date().getFullYear();
  const [holidays, setHolidays] = useState([]);
  const [open, setOpen] = useState(false);
  const [holidayDay, setHolidayDay] = useState('');
  const [holidayMonth, setHolidayMonth] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  const availableDays = holidayMonth
    ? Array.from(
      { length: getDaysInMonth(Number(holidayMonth), currentYear) },
      (_, index) => index + 1,
    )
    : [];

  const fetchHolidays = async () => {
    try {
      const res = await axios.get('/public_holidays');
      setHolidays(Array.isArray(res.data) ? res.data : []);
      setError('');
    } catch (fetchError) {
      setError(fetchError?.response?.data?.error || 'No se pudieron listar los feriados.');
    }
  };

  useEffect(() => {
    if (canRead) {
      fetchHolidays();
    }
  }, [canRead]);

  const resetForm = () => {
    setHolidayDay('');
    setHolidayMonth('');
    setDescription('');
  };

  const handleAdd = async () => {
    if (!holidayDay || !holidayMonth || !description.trim()) {
      setError('Debes completar día, mes y descripción.');
      return;
    }

    try {
      const normalizedDate = `${currentYear}-${String(holidayMonth).padStart(2, '0')}-${String(holidayDay).padStart(2, '0')}`;
      const params = new URLSearchParams({
        date: normalizedDate,
        description: description.trim(),
      });

      await axios.post(`/public_holidays?${params.toString()}`);
      setOpen(false);
      resetForm();
      fetchHolidays();
    } catch (saveError) {
      setError(saveError?.response?.data?.error || 'No se pudo guardar el feriado.');
    }
  };

  const handleDelete = async (holiday) => {
    const ok = await confirm({
      title: 'Quitar feriado',
      content: `¿Seguro que querés quitar el feriado "${holiday.description}"?`,
      confirmText: 'Quitar',
    });
    if (!ok) return;

    try {
      await axios.delete(`/public_holidays/delete?id=${holiday.id}`);
      fetchHolidays();
    } catch (deleteError) {
      setError(deleteError?.response?.data?.error || 'No se pudo eliminar el feriado.');
    }
  };

  if (!canRead) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6" color="error">No tienes permiso para ver esta sección.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      {ConfirmDialog}
      <Typography variant="h4" align="center" gutterBottom>
        Configurar Feriados
      </Typography>

      <Box sx={{ maxWidth: 900, mx: 'auto', mb: 3 }}>
        <Alert severity="info" icon={<CelebrationIcon fontSize="inherit" />}>
          Aquí se administran los feriados oficiales del sistema. Se guardan por <strong>día y mes</strong>.
          La validación se hace automáticamente contra el año actual.
        </Alert>
      </Box>

      {error && (
        <Box sx={{ maxWidth: 900, mx: 'auto', mb: 2 }}>
          <Alert severity="warning">{error}</Alert>
        </Box>
      )}

      {canWrite && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Box sx={{ width: '100%', maxWidth: 900, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                resetForm();
                setOpen(true);
              }}
            >
              Agregar feriado
            </Button>
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: '100%', maxWidth: 900 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Día / mes</TableCell>
                <TableCell>Descripción</TableCell>
                {canWrite && <TableCell align="right">Acciones</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {holidays.map((holiday) => (
                <TableRow key={holiday.id}>
                  <TableCell>{formatDayMonth(holiday.month, holiday.day_of_month)}</TableCell>
                  <TableCell>{holiday.description}</TableCell>
                  {canWrite && (
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleDelete(holiday)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {holidays.length === 0 && (
                <TableRow>
                  <TableCell colSpan={canWrite ? 3 : 2} align="center">
                    No hay feriados configurados.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>

      <Dialog open={open && canWrite} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuevo feriado</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 1 }}>
            <TextField
              select
              fullWidth
              label="Mes"
              value={holidayMonth}
              onChange={(event) => {
                setHolidayMonth(event.target.value);
                setHolidayDay('');
              }}
            >
              {MONTH_OPTIONS.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              label="Día"
              value={holidayDay}
              onChange={(event) => setHolidayDay(event.target.value)}
              disabled={!holidayMonth}
              helperText={!holidayMonth ? 'Primero selecciona el mes.' : 'Esto es lo que quedará guardado en el feriado.'}
            >
              {availableDays.map((day) => (
                <MenuItem key={day} value={day}>
                  {String(day).padStart(2, '0')}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <TextField
            margin="dense"
            label="Descripción"
            fullWidth
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={handleAdd} variant="contained">Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
